import React, { useEffect, useState, useCallback } from 'react';
import { Box, Card, CardContent, Typography, Container, CircularProgress, Divider, Chip, LinearProgress, IconButton, Modal, Paper, Button } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import axios, { AxiosError } from 'axios';
import LoginScreen from './components/LoginScreen';

interface Meeting {
  row: number;
  summary: string;
  attendees: string;
  category: string;
}

const CATEGORIES = {
  ArrowLeft: 'Investor',
  ArrowRight: 'Startup',
  ArrowUp: 'Operator',
  ArrowDown: 'Journalist',
  Space: 'Other',
  ShiftRight: 'Internal'
};

// Add a theme object for consistent colors
const theme = {
  colors: {
    primary: 'var(--color-primary)',
    secondary: 'var(--color-secondary)',
    background: 'var(--color-background)',
    surface: 'var(--color-surface)',
    text: {
      primary: 'var(--color-text-primary)',
      secondary: 'var(--color-text-secondary)',
    },
    border: 'var(--color-border)',
    success: 'var(--color-success)',
    error: 'var(--color-error)',
  },
  spacing: {
    xs: 'var(--space-xs)',
    sm: 'var(--space-sm)',
    md: 'var(--space-md)',
    lg: 'var(--space-lg)',
    xl: 'var(--space-xl)',
    '2xl': 'var(--space-2xl)',
  },
  shadows: {
    sm: 'var(--shadow-sm)',
    md: 'var(--shadow-md)',
    lg: 'var(--shadow-lg)',
  },
  transitions: {
    fast: 'var(--transition-fast)',
    normal: 'var(--transition-normal)',
    slow: 'var(--transition-slow)',
  },
  radius: {
    sm: 'var(--radius-sm)',
    md: 'var(--radius-md)',
    lg: 'var(--radius-lg)',
    full: 'var(--radius-full)',
  },
};

// Add this after the theme object
const getKeyboardIcon = (key: string) => {
  const icons = {
    ArrowLeft: '←',
    ArrowRight: '→',
    ArrowUp: '↑',
    ArrowDown: '↓',
    Space: '␣',
    ShiftRight: '⇧R',
    ShiftLeft: '⇧L'
  };
  return icons[key as keyof typeof icons] || key;
};

interface Statistics {
  lastActionTime: number;
  categorizedTimes: number[];
  categories: Record<string, number>;
}

// Add this near the top of the file, after the imports
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:8787/api'  // Development API URL
  : 'https://meeting-categorizer.antoine-f63.workers.dev/api'; // Production API URL

function App() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastKeyPressed, setLastKeyPressed] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : true;
  });
  const [stats, setStats] = useState<Statistics>({
    lastActionTime: Date.now(),
    categorizedTimes: [],
    categories: {}
  });
  const [history, setHistory] = useState<{
    meetings: Meeting[];
    index: number;
    timestamp: number;
  }[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('auth_token');
    return !!token;
  });
  const [authError, setAuthError] = useState<string>();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    try {
      setError(null);
      console.log('Attempting to fetch meetings from:', `${API_BASE_URL}/meetings`);
      
      const response = await axios.get(`${API_BASE_URL}/meetings`);
      console.log('API Response:', response);
      
      const meetings = response.data?.meetings || [];
      console.log('Total meetings loaded:', meetings.length);
      
      // Find first uncategorized valid meeting
      const firstUncategorizedIndex = meetings.findIndex((meeting: Meeting) => {
        const isValidMeeting = meeting.summary.trim() && meeting.attendees.trim();
        const needsCategorization = !meeting.category || meeting.category.trim() === '';
        
        if (isValidMeeting && needsCategorization) {
          console.log('Found uncategorized valid meeting:', {
            row: meeting.row,
            summary: meeting.summary.substring(0, 50) + (meeting.summary.length > 50 ? '...' : ''),
            attendees: meeting.attendees.substring(0, 50) + (meeting.attendees.length > 50 ? '...' : '')
          });
        }
        
        return isValidMeeting && needsCategorization;
      });
      
      console.log('First uncategorized valid meeting index:', firstUncategorizedIndex);
      if (firstUncategorizedIndex !== -1) {
        console.log('First uncategorized valid meeting details:', {
          arrayIndex: firstUncategorizedIndex,
          meeting: meetings[firstUncategorizedIndex]
        });
      }
      
      setMeetings(meetings);
      setCurrentIndex(firstUncategorizedIndex !== -1 ? firstUncategorizedIndex : meetings.length - 1);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setError('Failed to fetch meetings');
      setLoading(false);
    }
  };

  const addToHistory = (meetings: Meeting[], index: number) => {
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      return [...newHistory, {
        meetings: [...meetings],
        index,
        timestamp: Date.now()
      }];
    });
    setHistoryIndex(prev => prev + 1);
  };

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1];
      setMeetings(prevState.meetings);
      setCurrentIndex(prevState.index);
      setHistoryIndex(prev => prev - 1);
    }
  }, [history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1];
      setMeetings(nextState.meetings);
      setCurrentIndex(nextState.index);
      setHistoryIndex(prev => prev + 1);
    }
  }, [history, historyIndex]);

  const handleKeyDown = useCallback(async (event: KeyboardEvent) => {
    console.log('Key pressed:', event.code);
    setLastKeyPressed(event.code);
    setTimeout(() => setLastKeyPressed(null), 200);

    // Handle undo/redo
    if (event.metaKey || event.ctrlKey) {
      if (event.code === 'KeyZ') {
        if (event.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        return;
      }
    }

    // Handle category assignment
    let category = '';
    if (event.code === 'Space') category = CATEGORIES.Space;
    else if (event.code === 'ArrowLeft') category = CATEGORIES.ArrowLeft;
    else if (event.code === 'ArrowRight') category = CATEGORIES.ArrowRight;
    else if (event.code === 'ArrowUp') category = CATEGORIES.ArrowUp;
    else if (event.code === 'ArrowDown') category = CATEGORIES.ArrowDown;
    else if (event.code === 'ShiftRight') category = CATEGORIES.ShiftRight;

    if (category && currentIndex < meetings.length) {
      const meeting = meetings[currentIndex];
      console.log('Current meeting:', {
        arrayIndex: currentIndex,
        row: meeting.row,
        summary: meeting.summary,
        currentCategory: meeting.category,
        newCategory: category
      });
      
      try {
        console.log('Sending categorization request for meeting:', meeting.row);
        await axios.post(`${API_BASE_URL}/categorize`, {
          row: meeting.row,
          category
        });
        
        console.log('Successfully categorized meeting');
        updateStats(category);
        
        const updatedMeetings = [...meetings];
        updatedMeetings[currentIndex] = {
          ...updatedMeetings[currentIndex],
          category
        };
        
        // Find next uncategorized valid meeting after current index
        const nextUncategorized = updatedMeetings.findIndex(
          (m, i) => i > currentIndex && 
            m.summary.trim() && 
            m.attendees.trim() && 
            (!m.category || m.category.trim() === '')
        );
        
        console.log('Next uncategorized valid meeting:', nextUncategorized !== -1 ? {
          arrayIndex: nextUncategorized,
          meeting: updatedMeetings[nextUncategorized]
        } : 'No more uncategorized valid meetings');
        
        addToHistory(updatedMeetings, nextUncategorized === -1 ? currentIndex : nextUncategorized);
        
        setMeetings(updatedMeetings);
        if (nextUncategorized !== -1) {
          console.log('Moving to next uncategorized valid meeting:', {
            arrayIndex: nextUncategorized,
            meeting: updatedMeetings[nextUncategorized]
          });
          setCurrentIndex(nextUncategorized);
        }
      } catch (error) {
        console.error('Error categorizing meeting:', error);
      }
    }
  }, [currentIndex, meetings, handleUndo, handleRedo, history.length, historyIndex, addToHistory]);

  useEffect(() => {
    console.log('Adding keydown event listener');
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      console.log('Removing keydown event listener');
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Calculate progress
  const getCategorizedCount = () => {
    return meetings.filter(meeting => {
      const isValidMeeting = meeting.summary.trim() && meeting.attendees.trim();
      return isValidMeeting && meeting.category.trim();
    }).length;
  };

  const getValidMeetingsCount = () => {
    return meetings.filter(meeting => 
      meeting.summary.trim() && meeting.attendees.trim()
    ).length;
  };

  const getProgress = () => {
    const validCount = getValidMeetingsCount();
    if (validCount === 0) return 0;
    return (getCategorizedCount() / validCount) * 100;
  };

  const updateStats = (category: string) => {
    const now = Date.now();
    setStats(prev => {
      const timeTaken = now - prev.lastActionTime;
      return {
        lastActionTime: now,
        categorizedTimes: [...prev.categorizedTimes, timeTaken],
        categories: {
          ...prev.categories,
          [category]: (prev.categories[category] || 0) + 1
        }
      };
    });
  };

  const getAverageCategoryTime = () => {
    if (stats.categorizedTimes.length === 0) return 0;
    // Remove any times that are unreasonably long (e.g., breaks)
    const reasonableTimes = stats.categorizedTimes.filter(time => time < 30000); // Ignore times over 30 seconds
    if (reasonableTimes.length === 0) return 0;
    const avg = reasonableTimes.reduce((a, b) => a + b, 0) / reasonableTimes.length;
    return Math.round(avg / 1000); // Convert to seconds
  };

  const getMostCommonCategory = () => {
    const categories = stats.categories;
    return Object.entries(categories).reduce((a, b) => 
      (categories[a] || 0) > categories[b[0]] ? a : b[0], ''
    );
  };

  const handleLogin = (password: string) => {
    // In production, you should validate this against a secure backend
    if (password === process.env.REACT_APP_AUTH_PASSWORD) {
      localStorage.setItem('auth_token', 'authenticated');
      setIsAuthenticated(true);
      setAuthError(undefined);
    } else {
      setAuthError('Invalid password');
    }
  };

  if (!isAuthenticated) {
    return (
      <LoginScreen onLogin={handleLogin} error={authError} />
    );
  }

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ backgroundColor: theme.colors.background }}
        className="animate-fadeIn"
      >
        <CircularProgress 
          sx={{ 
            color: theme.colors.primary,
            marginBottom: theme.spacing.md,
          }} 
        />
        <Typography 
          variant="body1"
          sx={{ 
            color: theme.colors.text.secondary,
            fontWeight: 500,
          }}
        >
          Loading your meetings...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ backgroundColor: theme.colors.background }}
        className="animate-fadeIn"
      >
        <Typography 
          color="error" 
          variant="h6"
          sx={{ 
            marginBottom: theme.spacing.md,
            fontWeight: 600,
          }}
        >
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
          sx={{
            backgroundColor: theme.colors.primary,
            color: theme.colors.text.primary,
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            borderRadius: theme.radius.full,
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: theme.colors.primary,
              opacity: 0.9,
            }
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: theme.colors.background, 
      minHeight: '100vh',
      padding: theme.spacing.xl,
      transition: theme.transitions.normal,
    }}>
      <Container maxWidth="md">
        <Box 
          display="flex" 
          flexDirection="column" 
          gap={3}
          className="animate-fadeIn"
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontFamily: '"Space Grotesk", sans-serif',
                fontWeight: 700,
                color: theme.colors.primary,
                letterSpacing: '-0.03em',
                fontSize: '2.2rem',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -4,
                  left: 0,
                  width: '40%',
                  height: '3px',
                  background: `linear-gradient(90deg, ${theme.colors.primary}, transparent)`,
                  borderRadius: theme.radius.full,
                }
              }}
            >
              Meeting Categorizer
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                onClick={() => setIsDarkMode(!isDarkMode)}
                sx={{ 
                  color: theme.colors.text.secondary,
                  transition: theme.transitions.fast,
                  '&:hover': {
                    color: theme.colors.text.primary,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
              <IconButton 
                onClick={() => setShowHelp(true)}
                sx={{ 
                  color: theme.colors.text.secondary,
                  transition: theme.transitions.fast,
                  '&:hover': {
                    color: theme.colors.text.primary,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Box>
          </Box>
          
          <Box sx={{ 
            width: '100%',
            backgroundColor: theme.colors.surface,
            borderRadius: theme.radius.md,
            padding: theme.spacing.sm,
            display: 'flex', 
            alignItems: 'center',
            gap: theme.spacing.md,
          }}>
            <LinearProgress 
              variant="determinate" 
              value={getProgress()} 
              sx={{ 
                flexGrow: 1,
                height: 4,
                borderRadius: theme.radius.full,
                backgroundColor: 'rgba(127, 90, 240, 0.2)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: theme.radius.full,
                  backgroundColor: theme.colors.primary,
                  transition: theme.transitions.slow,
                }
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ color: theme.colors.text.secondary }}
            >
              {getCategorizedCount()} / {getValidMeetingsCount()} valid meetings
            </Typography>
          </Box>

          {meetings.length > 0 && currentIndex < meetings.length ? (
            <Card 
              sx={{ 
                width: '100%', 
                backgroundColor: theme.colors.surface,
                borderRadius: theme.radius.lg,
                border: `1px solid ${theme.colors.border}`,
                overflow: 'hidden',
                transition: theme.transitions.normal,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows.lg,
                },
              }}
              className="animate-fadeIn"
            >
              <CardContent sx={{ 
                padding: theme.spacing.xl,
                position: 'relative',
              }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    marginBottom: theme.spacing.lg,
                  }}
                >
                  {meetings[currentIndex].summary}
                </Typography>
                
                <Box sx={{ 
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                  borderRadius: theme.radius.md,
                  padding: theme.spacing.lg,
                  marginBottom: theme.spacing.lg,
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.colors.text.secondary,
                      marginBottom: theme.spacing.sm,
                    }}
                  >
                    Attendees
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing.xs,
                  }}>
                    {meetings[currentIndex].attendees.split(',').map((attendee, index) => (
                      <Box key={index} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing.xs,
                      }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: theme.colors.text.primary,
                            fontWeight: 500,
                          }}
                        >
                          {attendee.trim()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {meetings[currentIndex].category && (
                  <Chip 
                    label={meetings[currentIndex].category}
                    sx={{
                      backgroundColor: isDarkMode ? 'rgba(127, 90, 240, 0.2)' : 'rgba(127, 90, 240, 0.1)',
                      color: theme.colors.primary,
                      fontWeight: 600,
                      marginBottom: theme.spacing.md,
                    }}
                  />
                )}
              </CardContent>
            </Card>
          ) : (
            <Box 
              sx={{ 
                backgroundColor: theme.colors.surface,
                borderRadius: theme.radius.lg,
                padding: theme.spacing.xl,
                textAlign: 'center',
                width: '100%',
              }}
              className="animate-fadeIn"
            >
              <Typography 
                variant="h5"
                sx={{ 
                  color: theme.colors.text.primary,
                  fontWeight: 600,
                }}
              >
                {meetings.length === 0 ? 'No meetings found' : 'All meetings have been categorized!'}
              </Typography>
            </Box>
          )}

          <Box sx={{ 
            width: '100%',
            backgroundColor: theme.colors.surface,
            borderRadius: theme.radius.lg,
            padding: theme.spacing.lg,
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: theme.colors.text.primary,
                fontWeight: 600,
                marginBottom: theme.spacing.md,
              }}
            >
              Keyboard Controls
            </Typography>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.md,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              pb: theme.spacing.sm,
              '::-webkit-scrollbar': {
                height: '4px',
              },
            }}>
              {Object.entries(CATEGORIES).map(([key, value]) => (
                <React.Fragment key={key}>
                  {key !== Object.keys(CATEGORIES)[0] && (
                    <Box sx={{ 
                      width: '1px', 
                      height: '20px', 
                      backgroundColor: theme.colors.border,
                      flexShrink: 0,
                    }} />
                  )}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: theme.spacing.sm,
                    flexShrink: 0,
                  }}>
                    <Box sx={{ 
                      backgroundColor: lastKeyPressed === key ? 
                        isDarkMode ? 'rgba(127, 90, 240, 0.2)' : 'rgba(127, 90, 240, 0.1)' : 
                        isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                      borderRadius: theme.radius.sm,
                      fontSize: '0.9em',
                      fontWeight: 600,
                      minWidth: '24px',
                      textAlign: 'center',
                      color: theme.colors.primary,
                      transition: theme.transitions.fast,
                      transform: lastKeyPressed === key ? 'translateY(-2px)' : 'none',
                    }}>
                      {getKeyboardIcon(key)}
                    </Box>
                    <Typography 
                      sx={{ 
                        color: theme.colors.text.primary,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </Box>

          <Box sx={{ 
            width: '100%',
            backgroundColor: theme.colors.surface,
            borderRadius: theme.radius.lg,
            padding: theme.spacing.lg,
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: theme.colors.text.primary,
                fontWeight: 600,
                marginBottom: theme.spacing.md,
              }}
            >
              Statistics
            </Typography>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.xl,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              pb: theme.spacing.sm,
              '::-webkit-scrollbar': {
                height: '4px',
              },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ color: theme.colors.text.secondary }}
                >
                  Completion:
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ color: theme.colors.text.primary, fontWeight: 500 }}
                >
                  {Math.round(getProgress())}%
                </Typography>
              </Box>

              <Box sx={{ 
                width: '1px', 
                height: '20px', 
                backgroundColor: theme.colors.border 
              }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ color: theme.colors.text.secondary }}
                >
                  Average:
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ color: theme.colors.text.primary, fontWeight: 500 }}
                >
                  {getAverageCategoryTime()}s
                </Typography>
              </Box>

              {getMostCommonCategory() && (
                <>
                  <Box sx={{ 
                    width: '1px', 
                    height: '20px', 
                    backgroundColor: theme.colors.border 
                  }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ color: theme.colors.text.secondary }}
                    >
                      Most used:
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ color: theme.colors.text.primary, fontWeight: 500 }}
                    >
                      {getMostCommonCategory()} ({stats.categories[getMostCommonCategory()] || 0})
                    </Typography>
                  </Box>
                </>
              )}

              <Box sx={{ 
                width: '1px', 
                height: '20px', 
                backgroundColor: theme.colors.border 
              }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ color: theme.colors.text.secondary }}
                >
                  Remaining:
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ color: theme.colors.text.primary, fontWeight: 500 }}
                >
                  {getValidMeetingsCount() - getCategorizedCount()}
                </Typography>
              </Box>

              <Box sx={{ 
                width: '1px', 
                height: '20px', 
                backgroundColor: theme.colors.border 
              }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ color: theme.colors.text.secondary }}
                >
                  Est. time left:
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ color: theme.colors.text.primary, fontWeight: 500 }}
                >
                  {Math.round((getValidMeetingsCount() - getCategorizedCount()) * getAverageCategoryTime() / 60)}m
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      <Modal
        open={showHelp}
        onClose={() => setShowHelp(false)}
        aria-labelledby="help-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '600px',
          backgroundColor: theme.colors.surface,
          borderRadius: theme.radius.lg,
          padding: theme.spacing.xl,
          border: `1px solid ${theme.colors.border}`,
          boxShadow: theme.shadows.lg,
        }}>
          <Typography 
            variant="h5" 
            component="h2" 
            sx={{ 
              color: theme.colors.text.primary,
              fontWeight: 600,
              marginBottom: theme.spacing.xl,
            }}
          >
            Keyboard Shortcuts
          </Typography>
          <Box sx={{ display: 'grid', gap: theme.spacing.lg }}>
            <Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme.colors.text.primary,
                  fontWeight: 600,
                  marginBottom: theme.spacing.md,
                }}
              >
                Categories
              </Typography>
              <Box sx={{ display: 'grid', gap: theme.spacing.md }}>
                {Object.entries(CATEGORIES).map(([key, value]) => (
                  <Box 
                    key={key} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: theme.spacing.md,
                    }}
                  >
                    <Chip 
                      label={getKeyboardIcon(key)} 
                      size="small"
                      sx={{ 
                        minWidth: 60,
                        backgroundColor: 'rgba(127, 90, 240, 0.2)',
                        color: theme.colors.primary,
                        fontWeight: 600,
                      }}
                    />
                    <Typography sx={{ color: theme.colors.text.primary }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme.colors.text.primary,
                  fontWeight: 600,
                  marginBottom: theme.spacing.md,
                }}
              >
                Navigation
              </Typography>
              <Box sx={{ display: 'grid', gap: theme.spacing.md }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
                  <Chip 
                    label="⇧" 
                    size="small" 
                    sx={{ 
                      minWidth: 60,
                      backgroundColor: 'rgba(127, 90, 240, 0.2)',
                      color: theme.colors.primary,
                      fontWeight: 600,
                    }} 
                  />
                  <Typography sx={{ color: theme.colors.text.primary }}>
                    Go Back
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default App;
